/* eslint-disable id-length, max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Alert, Button, Modal, Row, Col } from '@storable/canaveral-components';
import { Tab, Tabs, Form } from 'react-bootstrap';
import { FACILITIES_NEARBY_LIMIT } from '../../../../../constants/facilitiesNearby';
import FacilitySelect from '../../../../../admin/containers/FacilitySelect';
import FacilitiesNearbyCurrentUserMapEditor from '../../../../containers/FacilitiesNearbyCurrentUserMapEditor';
import FacilitiesNearbyFacilityMapEditor from '../../../../containers/FacilitiesNearbyFacilityMapEditor';
import FacilitiesNearbyCurrentUserLegend from '../../../../containers/FacilitiesNearbyCurrentUserLegend';
import FacilitiesNearbyFacilityLegend from '../../../../containers/FacilitiesNearbyFacilityLegend';
import {
  NAME,
  OPTION_NEAR_FACILITY,
  OPTION_NEAR_CURRENT_USER,
  OPTION_DEFAULT,
  FACILITY_DIST,
  HEADER_TEXT,
} from '../properties';
import styles from './styles.scss';

const FACILITIES_TAB = 'facilities';
const MAP_TAB = 'map';

const propTypes = {
  center: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  facilityDistance: PropTypes.number,
  headerText: PropTypes.string,
  onCancelEdit: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  pageFacilityId: PropTypes.string,
  selectedFacilityId: PropTypes.string,
  selectedOption: PropTypes.string,
  zoom: PropTypes.number,
};

const defaultProps = {
  center: undefined,
  facilityDistance: FACILITY_DIST,
  headerText: HEADER_TEXT,
  pageFacilityId: undefined,
  selectedFacilityId: undefined,
  selectedOption: OPTION_DEFAULT,
  zoom: undefined,
};

const Editor = ({
  center: centerFromProps,
  facilityDistance,
  headerText,
  onCancelEdit,
  onFinishEdit,
  pageFacilityId,
  selectedFacilityId,
  selectedOption,
  zoom: zoomFromProps,
}) => {
  const [tab, setTab] = useState(FACILITIES_TAB);
  const [zoom, setZoom] = useState(zoomFromProps);
  const [center, setCenter] = useState(centerFromProps);
  const [data, setData] = useState({
    headerText,
    selectedOption,
    facilityDistance,
    selectedFacilityId,
  });

  const onSelect = selectedTab => setTab(selectedTab);

  const onChange = ({ target: { name, value } }) => setData({ ...data, [name]: value });
  const onFacilityChange = ({ value }) => setData({ ...data, selectedFacilityId: value });

  const onSubmit = () => onFinishEdit({
    ...data,
    center,
    zoom,
  });

  const modalId = 'nearby-locations-widget-editor';
  const tabsId = 'nearby-locations-widget-editor-tabs';

  const nearCurrentUser = data.selectedOption === OPTION_NEAR_CURRENT_USER;
  const nearFacility = data.selectedOption === OPTION_NEAR_FACILITY;

  const disabled = !data.selectedOption
    || (nearFacility && (!data.selectedFacilityId || !data.facilityDistance));

  const facilityId = nearFacility ? data.selectedFacilityId : null;
  const maxDistanceInMiles = nearFacility && facilityId
    ? data.facilityDistance && parseInt(data.facilityDistance, 10)
    : null;
  const primaryFacilityId = pageFacilityId;

  let mapMarkerList;
  let mapEditor;

  if (nearCurrentUser) {
    mapMarkerList = (
      <FacilitiesNearbyCurrentUserLegend
        primaryFacilityId={primaryFacilityId}
      />
    );

    mapEditor = (
      <FacilitiesNearbyCurrentUserMapEditor
        disabled
        primaryFacilityId={primaryFacilityId}
        allowConfiguration={false}
      />
    );
  } else if (facilityId) {
    mapMarkerList = (
      <FacilitiesNearbyFacilityLegend
        facilityId={facilityId}
        maxDistanceInMiles={maxDistanceInMiles}
        primaryFacilityId={primaryFacilityId}
      />
    );

    mapEditor = (
      <FacilitiesNearbyFacilityMapEditor
        initialCenter={center}
        initialZoom={zoom}
        facilityId={facilityId}
        maxDistanceInMiles={maxDistanceInMiles}
        onPan={setCenter}
        onZoom={setZoom}
        primaryFacilityId={primaryFacilityId}
        allowConfiguration={false}
      />
    );
  }

  return (
    <Modal
      show
      size="xl"
      aria-labelledby={modalId}
      onDismiss={onCancelEdit}
    >
      <Modal.Header>
        <Modal.Title id={modalId}>{NAME}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12}>
            <Tabs
              id={tabsId}
              activeKey={tab}
              onSelect={onSelect}
              defaultActiveKey={FACILITIES_TAB}
            >
              <Tab
                title="Facilities"
                eventKey={FACILITIES_TAB}
                className={styles.tab}
              >
                <Row className={styles.formCustom}>
                  <Col xs={7}>
                    <div className={styles.title}>
                      Settings
                    </div>
                    <form>
                      <Form.Group controlId="headerText">
                        <Form.Label className={styles.headerText}>Header text</Form.Label>
                        <Form.Control
                          type="text"
                          name="headerText"
                          onChange={onChange}
                          value={data.headerText}
                        />
                        <Form.Text>
                          This displays above the list of facilities.
                        </Form.Text>
                      </Form.Group>
                      <Form.Group controlId="selectedOption">
                        <Form.Label>
                          Select which facilities to display
                        </Form.Label>
                        <Form.Check
                          id="nearCurrentUser"
                          type="radio"
                          onChange={onChange}
                          name="selectedOption"
                          checked={nearCurrentUser}
                          value={OPTION_NEAR_CURRENT_USER}
                          label={`Show up to ${FACILITIES_NEARBY_LIMIT} facilities nearest the user's location`}
                        />
                        <Form.Check
                          id="nearFacility"
                          type="radio"
                          onChange={onChange}
                          name="selectedOption"
                          checked={nearFacility}
                          value={OPTION_NEAR_FACILITY}
                          className={styles.inline}
                          label={`Show up to ${FACILITIES_NEARBY_LIMIT} facilities within \u00A0`}
                        />
                        <Form.Control
                          id="facilityDistance"
                          type="number"
                          onChange={onChange}
                          name="facilityDistance"
                          disabled={!nearFacility}
                          value={data.facilityDistance}
                          className={styles.facilityDistance}
                        />
                        <div className={cx('radio', styles.inline)}>
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label className={styles.label}>&nbsp;miles of</label>
                        </div>
                        <FacilitySelect
                          clearable={false}
                          disabled={!nearFacility}
                          onChange={onFacilityChange}
                          value={data.selectedFacilityId}
                        />
                        <Form.Text>
                          Greyed out facilities do not have a live page.
                        </Form.Text>
                      </Form.Group>
                    </form>
                  </Col>
                  <Col xs={5}>
                    <div className={styles.title}>
                      Facilities
                    </div>
                    <div className={styles.facilities}>
                      {mapMarkerList}
                    </div>
                  </Col>
                </Row>
              </Tab>
              <Tab
                title="Map"
                eventKey={MAP_TAB}
                className={styles.tab}
              >
                <Row>
                  <Col xs={7}>
                    <div className={styles.title}>
                      Adjust the zoom level and positioning of your map.
                    </div>
                    <Form.Text>
                      If this widget is placed on a facility page, the facility’s address will be marked with a special pin.
                    </Form.Text>
                    {mapEditor}
                  </Col>
                  <Col xs={5}>
                    <div className={styles.title}>
                      Facilities
                    </div>
                    <div className={styles.facilities}>
                      {mapMarkerList}
                    </div>
                    {nearCurrentUser && (
                      <Alert context="info">
                        The zoom and positioning cannot be edited because the facilities displayed vary
                        based on the website user’s location.
                      </Alert>
                    )}
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {tab === FACILITIES_TAB ? (
          <React.Fragment>
            <Button variant="neutral" size="sm" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button variant="positive" size="sm" onClick={() => setTab(MAP_TAB)}>
              Next
            </Button>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Button variant="neutral" size="sm" onClick={() => setTab(FACILITIES_TAB)}>
              Back
            </Button>
            <Button variant="positive" size="sm" disabled={disabled} onClick={onSubmit}>
              Submit
            </Button>
          </React.Fragment>
        )}
      </Modal.Footer>
    </Modal>
  );
};

Editor.propTypes = propTypes;
Editor.defaultProps = defaultProps;

export default Editor;
